import { useEffect, useMemo, useState } from "react";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  LayoutTableIcon,
  ViewIcon,
  ViewOffSlashIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { BaseInput } from "shared/components/forms/BaseInput";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  InputSize,
  Show
} from "shared/components";
import {
  Popover,
  PopoverTrigger,
  PopoverContent
} from "shared/components/Popover";
import type { ColumnVisibilityItem } from "providers/Auth";
import { ColumnRow } from "./ColumnRow";
import {
  SECTIONS,
  applyMenuDrag,
  isLockedColumn,
  matchesSearch,
  partitionColumns,
  toggleColumn
} from "./utils";

type ColumnsManagerMenuProps = {
  columns: ColumnVisibilityItem[];
  onChange: (columns: ColumnVisibilityItem[]) => void;
  onClose?: () => void;
  headingLabel?: string;
  /** Columns the table fixes to an edge: they can't be dragged but can still be hidden. */
  fixedIds?: string[];
  /** Shows "Reset to default" at the bottom of the menu. */
  onReset?: () => void;
  disabled?: boolean;
};

const NO_FIXED_IDS: string[] = [];

const hiddenDropZoneClass = (isEmpty: boolean, isDraggingOver: boolean) => {
  if (!isEmpty) return "min-h-[8px]";
  return isDraggingOver ? "min-h-10 bg-alpha-20" : "min-h-10";
};

export const ColumnsManagerMenu = ({
  columns,
  onChange,
  onClose,
  headingLabel,
  fixedIds = NO_FIXED_IDS,
  onReset,
  disabled
}: ColumnsManagerMenuProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const fixed = useMemo(() => new Set(fixedIds), [fixedIds]);
  const { visible, hidden } = useMemo(
    () => partitionColumns(columns),
    [columns]
  );
  const visibleCount = visible.length;

  useEffect(() => {
    if (!open) {
      setSearch("");
      onClose?.();
    }
    // onClose is intentionally omitted; we only want to fire when `open` flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Locked rows sit outside the drop areas (pinned above them, mirroring the columns fixed to the
  // table's left edge), so drag indices are relative to the draggable rows only.
  const draggable = useMemo(
    () => ({
      [SECTIONS.Visible]: visible.filter((c) => !isLockedColumn(c, fixed)),
      [SECTIONS.Hidden]: hidden.filter((c) => !isLockedColumn(c, fixed))
    }),
    [visible, hidden, fixed]
  );

  const handleDragEnd = ({ source, destination }: DropResult) => {
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    onChange(
      applyMenuDrag(columns, {
        draggable,
        from: {
          section: source.droppableId as typeof SECTIONS.Visible,
          index: source.index
        },
        to: {
          section: destination.droppableId as typeof SECTIONS.Visible,
          index: destination.index
        }
      })
    );
  };

  const handleToggle = (id: string) => onChange(toggleColumn(columns, id));

  const filter = (list: ColumnVisibilityItem[]) =>
    list.filter((c) => matchesSearch(c, search));
  const lockedVisible = filter(visible.filter((c) => isLockedColumn(c, fixed)));
  const lockedHidden = filter(hidden.filter((c) => isLockedColumn(c, fixed)));
  const draggableVisible = filter(draggable[SECTIONS.Visible]);
  const draggableHidden = filter(draggable[SECTIONS.Hidden]);
  const isSearching = Boolean(search);

  return (
    <Popover placement="bottom-end" open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className={`flex items-center gap-2 h-9 px-3 rounded-lg border bg-white text-sm font-medium text-content-1000 transition-colors hover:bg-alpha-20 disabled:cursor-not-allowed disabled:opacity-50 ${
            open ? "border-content-1000" : "border-alpha-100"
          }`}
        >
          <HugeiconsIcon
            icon={LayoutTableIcon}
            size={16}
            strokeWidth={2}
            className="text-content-700"
          />
          <span>Columns</span>
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-md bg-primary-500 text-xs font-semibold text-white">
            {visibleCount}
          </span>
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            size={16}
            strokeWidth={2}
            className={`text-content-500 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent className="mt-2 z-20 w-[320px] max-h-[70vh] rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden flex flex-col">
        {headingLabel ? (
          <h3 className="shrink-0 px-4 pt-3 pb-2 text-sm font-medium text-content-500">
            {headingLabel}
          </h3>
        ) : null}

        <div className="shrink-0 px-3 pt-3 pb-2">
          <BaseInput
            id="columns-manager-search"
            placeholder="Search"
            value={search}
            onChange={(value) => setSearch(String(value))}
            size={InputSize.SM}
            className="!mb-0"
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <DragDropContext onDragEnd={handleDragEnd}>
            <SectionHeader icon={ViewIcon} label="Visible" />
            {lockedVisible.map((column) => (
              <ColumnRow
                key={column.id}
                column={column}
                index={0}
                draggableId={`col-${column.id}`}
                isDragDisabled
                isFixed
                onToggle={handleToggle}
              />
            ))}
            <Droppable droppableId={SECTIONS.Visible}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="min-h-[8px]"
                >
                  {draggableVisible.map((column, idx) => (
                    <ColumnRow
                      key={column.id}
                      column={column}
                      index={idx}
                      draggableId={`col-${column.id}`}
                      isDragDisabled={isSearching}
                      onToggle={handleToggle}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            <SectionHeader icon={ViewOffSlashIcon} label="Hidden" />
            {lockedHidden.map((column) => (
              <ColumnRow
                key={column.id}
                column={column}
                index={0}
                draggableId={`col-${column.id}`}
                isDragDisabled
                isFixed
                onToggle={handleToggle}
              />
            ))}
            <Droppable droppableId={SECTIONS.Hidden}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={hiddenDropZoneClass(
                    draggable[SECTIONS.Hidden].length === 0,
                    snapshot.isDraggingOver
                  )}
                >
                  {/* With nothing hidden yet, give the drop target a real size and a hint;
                      an 8px strip at the bottom of a long list is too hard to hit. */}
                  <Show
                    if={
                      draggable[SECTIONS.Hidden].length === 0 &&
                      !snapshot.isDraggingOver &&
                      !isSearching
                    }
                  >
                    <p className="flex h-10 items-center px-4 text-xs font-medium text-content-300">
                      Drag a column here to hide it
                    </p>
                  </Show>
                  {draggableHidden.map((column, idx) => (
                    <ColumnRow
                      key={column.id}
                      column={column}
                      index={idx}
                      draggableId={`col-${column.id}`}
                      isDragDisabled={isSearching}
                      onToggle={handleToggle}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        <Show if={Boolean(onReset)}>
          <div className="shrink-0 border-t border-alpha-100 px-2 py-2">
            <Button
              variant={ButtonVariant.TEXT}
              size={ButtonSize.SM}
              onClick={() => onReset?.()}
            >
              Reset to default
            </Button>
          </div>
        </Show>
      </PopoverContent>
    </Popover>
  );
};

type SectionHeaderProps = {
  icon: Parameters<typeof HugeiconsIcon>[0]["icon"];
  label: string;
};

const SectionHeader = ({ icon, label }: SectionHeaderProps) => (
  <div className="flex items-center gap-2 px-4 h-9 bg-alpha-20 border-y border-alpha-100 text-sm font-medium text-content-500">
    <HugeiconsIcon icon={icon} size={16} strokeWidth={2} />
    <span>{label}</span>
  </div>
);
